import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { NTA_COLORS } from '../constants';
import {
  ntaBodyCss,
  ntaIntroCss,
  ntaListMarkerCss,
  ntaNoteCss,
  ntaPageCss,
  ntaSectionTitleCss
} from '../typography';
import { NTA_ITEM_TYPE } from './ntaStructuredInstructions';
import NtaPaletteLegend from './NtaPaletteLegend';
import NtaScrollArrowsHint from './NtaScrollArrowsHint';

const SectionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  ${ntaPageCss}
`;

const SectionBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-top: ${({ $first }) => ($first ? '24px' : '0')};
`;

const Intro = styled.p`
  margin: 0;
  padding-bottom: 8px;
  text-align: center;
  ${ntaIntroCss}
`;

const UnderlinedTitle = styled.h2`
  margin: 0;
  padding-bottom: 8px;
  color: ${NTA_COLORS.textPrimary};
  ${ntaSectionTitleCss}
`;

const InstructionList = styled.ol`
  margin: 0;
  padding: 0 0 0 24px;
  list-style-position: outside;
  ${ntaBodyCss}
`;

const InstructionItem = styled.li`
  margin-bottom: 8px;
  padding-left: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubStepList = styled.div`
  margin-top: 0;
`;

const SubStepRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding-left: 48px;
  margin-top: 0;
  ${ntaBodyCss}
`;

const SubStepLabel = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  ${ntaListMarkerCss}
`;

const SubStepText = styled.p`
  margin: 0;
  flex: 1;
  min-width: 0;
  ${ntaBodyCss}
`;

const LanguageNote = styled.p`
  margin: 0;
  padding: 16px;
  border-radius: 12px;
  background: ${NTA_COLORS.white};
  ${ntaNoteCss}
`;

const renderSubSteps = (items, labels = ['a.', 'b.', 'c.', 'd.', 'e.']) =>
  (items || []).map((text, index) => (
    <SubStepRow key={`${text}-${index}`}>
      <SubStepLabel>
        {labels[index] ?? `${String.fromCharCode(97 + index)}.`}
      </SubStepLabel>
      <SubStepText>{text}</SubStepText>
    </SubStepRow>
  ));

const renderItemContent = (item) => {
  switch (item.type) {
    case NTA_ITEM_TYPE.SCROLL_ARROWS:
      return <NtaScrollArrowsHint />;
    case NTA_ITEM_TYPE.PALETTE_LEGEND:
      return <NtaPaletteLegend />;
    case NTA_ITEM_TYPE.TEXT_WITH_SUBSTEPS:
      return (
        <>
          {item.text}
          <SubStepList>{renderSubSteps(item.substeps)}</SubStepList>
        </>
      );
    case NTA_ITEM_TYPE.TEXT:
    default:
      return item.text;
  }
};

const NtaStructuredInstructionsView = ({ payload }) => {
  let n = 0;
  const nextValue = () => {
    n += 1;
    return n;
  };

  return (
    <SectionsColumn>
      {(payload.sections || []).map((section, sectionIndex) => (
        <SectionBlock key={section.id} $first={sectionIndex === 0}>
          {sectionIndex === 0 && payload.intro ? (
            <Intro>{payload.intro}</Intro>
          ) : null}
          <UnderlinedTitle>{section.title}</UnderlinedTitle>
          <InstructionList>
            {(section.items || []).map((item) => (
              <InstructionItem key={item.id} value={nextValue()}>
                {renderItemContent(item)}
              </InstructionItem>
            ))}
          </InstructionList>
        </SectionBlock>
      ))}
      {payload.languageNote ? (
        <LanguageNote>{payload.languageNote}</LanguageNote>
      ) : null}
    </SectionsColumn>
  );
};

export default NtaStructuredInstructionsView;

NtaStructuredInstructionsView.propTypes = {
  payload: PropTypes.shape({
    intro: PropTypes.string,
    languageNote: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.object)
  }).isRequired
};
